/**
 * The founder's editable inputs, over the analysis rather than on top of it.
 *
 * WHY THIS IS A MODAL. Opening setup used to insert the forms into the report
 * scroll, above the analysis — so answering a blocked field pushed the thing you
 * came to read off the screen, and a founder who opened the form to check what
 * was being asked had to scroll past a dozen inputs to get back. The reminder
 * button is the whole of the on-page footprint; the form is a place you go and
 * come back from.
 *
 * THREE TARGETS, ONE FRAME:
 *
 *   `setup`       — everything blocking this analysis. Opened by the reminder
 *                   button and by `?setup=1`.
 *   `assumptions` — the market figures alone. Opened by "Edit assumptions" on a
 *                   published market report.
 *   `thesis`      — the exit thesis alone. Opened by "Edit thesis".
 *
 * The focused targets exist because editing a published report's inputs is a
 * different act from unblocking a refused one. A founder who wants to revise one
 * number should not be handed the whole setup questionnaire.
 *
 * WHAT THE EDITORS ARE SEEDED WITH. The focused targets edit the LATEST version;
 * the report keeps showing the version it BOUND. Those are different objects on
 * purpose — an assessment is a statement about the inputs it read, and reseeding
 * the editor from the bound version would make every revision look like an edit
 * to history.
 *
 * TWO SAVES IN `setup`, DELIBERATELY. Market Validation can need both a
 * strategy-context version and a bound market-assumptions version, and those are
 * two immutable documents with two different endpoints — not one form split in
 * half. Each keeps its own save so a founder can commit one without the other.
 * `AppModal.Actions` carries the modal's own action, which is leaving.
 *
 * A successful save closes it. The draft refetches, readiness moves, and the
 * reminder button disappears on its own — so "did that work?" is answered by the
 * screen the founder is returned to rather than by a toast. A save that FAILS
 * keeps the modal open with everything typed still in it, and says why.
 */

import React from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { AlertTriangle } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { getUserMessage, isRetryable } from '@/lib/errors';
import type { AnalysisType, MarketAssumptions, ExitThesis } from '@/lib/strategic-analysis/types';
import { ANALYSIS_LABELS } from '@/lib/strategic-analysis/types';
import type { ContextSelection, FounderInput } from './StrategyContextEditor';
import { StrategyContextEditor } from './StrategyContextEditor';
import { MarketAssumptionsEditor } from './MarketAssumptionsEditor';
import { ExitThesisEditor } from './ExitThesisEditor';

/** Wider than the default 480: a low/base/high triple needs the room. */
const SETUP_MAX_WIDTH = 620;

/** What the founder opened. */
export type SetupTarget = 'setup' | 'assumptions' | 'thesis';

/**
 * A latest-version fetch, in the four states that matter to a form.
 *
 * `isPending` and `isError` are kept apart from `data` on purpose: React Query
 * reports `data === undefined` for BOTH "still loading" and "loaded, and the
 * row does not exist", and those must not open the same screen. One is a
 * spinner; the other is an empty form for a document that has never been saved.
 */
export interface LatestQuery<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: T | null | undefined;
  refetch: () => void;
}

const TITLES: Record<Exclude<SetupTarget, 'setup'>, string> = {
  assumptions: 'Your market assumptions',
  thesis: 'Your exit thesis',
};

interface AnalysisSetupModalProps {
  target: SetupTarget | null;
  onClose: () => void;
  analysisType: AnalysisType;

  /** The strategy context, when something on it is still blocking. */
  draft: React.ComponentProps<typeof StrategyContextEditor>['draft'] | null;
  showContextEditor: boolean;
  blockingFields: string[];
  isSavingContext: boolean;
  onSaveContext: (selections: ContextSelection[], founderInputs: FounderInput[]) => void;

  /**
   * The LATEST versions — what an edit starts from. Never the bound ones.
   *
   * The whole QUERY, not just its data. An editor seeds its fields from props
   * once, in a `useState` initialiser, so mounting it against `data ?? null`
   * while the fetch is still in flight produces a blank form that never fills
   * in when the answer lands — the founder then saves a new version over their
   * own figures, having been shown none of them. Nothing mounts until the query
   * has resolved, and `null` after resolution means a genuinely new document.
   */
  assumptionsQuery: LatestQuery<MarketAssumptions>;
  isSavingAssumptions: boolean;
  onSaveAssumptions: (assumptions: MarketAssumptions) => void;

  thesisQuery: LatestQuery<ExitThesis>;
  isSavingThesis: boolean;
  onSaveThesis: (thesis: ExitThesis) => void;

  /** The failure of whichever save was last attempted for this target. */
  saveError?: unknown;
}

export function AnalysisSetupModal({
  target,
  onClose,
  analysisType,
  draft,
  showContextEditor,
  blockingFields,
  isSavingContext,
  onSaveContext,
  assumptionsQuery,
  isSavingAssumptions,
  onSaveAssumptions,
  thesisQuery,
  isSavingThesis,
  onSaveThesis,
  saveError,
}: AnalysisSetupModalProps) {
  if (!target) return null;

  // In `setup` the bound editor appears only on the analysis that reads it. In a
  // focused target the founder asked for that editor by name.
  const showAssumptions = target === 'assumptions' ||
    (target === 'setup' && analysisType === 'market_validation');
  const showThesis = target === 'thesis' ||
    (target === 'setup' && analysisType === 'exit_strategy');
  const showContext = target === 'setup' && showContextEditor;

  return (
    <AppModal
      visible
      onClose={onClose}
      title={target === 'setup' ? `${ANALYSIS_LABELS[analysisType]} inputs` : TITLES[target]}
      maxWidth={SETUP_MAX_WIDTH}
      footer={
        <AppModal.Actions>
          <ModalAction label="Close" onPress={onClose} variant="neutral" />
        </AppModal.Actions>
      }
    >
      {/* AppModal owns the ScrollView — this is plain content, never another
          scroller nested inside it. */}
      <View className="gap-6">
        {/* SETUP FIRST. The strategy context is the shared, versioned view every
            analysis reads; the bound editor answers one. Asking a founder to
            bind assumptions before the market they apply to had been defined
            was backwards. */}
        {showContext && draft && (
          <StrategyContextEditor
            draft={draft}
            canEdit
            isSaving={isSavingContext}
            focusFields={blockingFields.length > 0 ? blockingFields : null}
            onSave={onSaveContext}
          />
        )}

        {showAssumptions && (
          <LatestGate query={assumptionsQuery} label="market assumptions">
            {(assumptions) => (
              <MarketAssumptionsEditor
                assumptions={assumptions}
                canEdit
                isSaving={isSavingAssumptions}
                onSave={onSaveAssumptions}
              />
            )}
          </LatestGate>
        )}

        {showThesis && (
          <LatestGate query={thesisQuery} label="exit thesis">
            {(thesis) => (
              <ExitThesisEditor
                thesis={thesis}
                canEdit
                isSaving={isSavingThesis}
                onSave={onSaveThesis}
              />
            )}
          </LatestGate>
        )}

        <SaveFailure error={saveError} />
      </View>
    </AppModal>
  );
}

/**
 * Nothing until the answer is in.
 *
 * The three resolved outcomes are kept distinct because they are three
 * different screens: a fetch in flight is a spinner, a failed fetch is a
 * sanitized reason plus a way to try again, and a resolved `null` is an empty
 * form for a document that has never been saved.
 *
 * The failure path never shows the editor. Offering a blank form after a failed
 * read would invite a founder to re-enter figures they already have, and save
 * them as a NEW version over a set nobody could read back.
 */
function LatestGate<T>({
  query,
  label,
  children,
}: {
  query: LatestQuery<T>;
  label: string;
  children: (data: T | null) => React.ReactNode;
}) {
  const colors = useThemeColors();

  if (query.isPending) {
    return (
      <View className="items-center gap-3 py-8" accessibilityRole="progressbar">
        <ActivityIndicator color={colors.primary[600]} />
        <Text className="text-[12px]" style={{ color: colors.text.secondary }}>
          {`Loading your ${label}…`}
        </Text>
      </View>
    );
  }

  if (query.isError) {
    return (
      <View
        className="rounded-2xl p-4 gap-2"
        style={{ backgroundColor: colors.traffic.redLight, overflow: 'hidden' }}
        accessibilityRole="alert"
      >
        <View className="flex-row items-center gap-2">
          <AlertTriangle size={16} color={colors.error} />
          <Text
            numberOfLines={1}
            className="flex-1 text-[13px] font-semibold"
            style={{ color: colors.error }}
          >
            {`Could not load your ${label}`}
          </Text>
        </View>
        <Text className="text-[12px] leading-4" style={{ color: colors.text.primary }}>
          {getUserMessage(query.error) || 'Something went wrong reading your saved version.'}
        </Text>
        <Text className="text-[11px] leading-4" style={{ color: colors.text.secondary }}>
          Nothing has been changed. The form stays closed until this loads, so a new version cannot
          be saved over figures nobody could read back.
        </Text>
        <View
          className="rounded-xl overflow-hidden self-start"
          style={{ height: 40, backgroundColor: colors.surface.primary }}
        >
          <AnimatedPressable
            onPress={() => query.refetch()}
            accessibilityRole="button"
            accessibilityLabel={`Retry loading your ${label}`}
            className="items-center justify-center px-4"
            style={{ flex: 1 }}
          >
            <Text numberOfLines={1} className="text-[13px] font-semibold" style={{ color: colors.error }}>
              Try again
            </Text>
          </AnimatedPressable>
        </View>
      </View>
    );
  }

  // Resolved. `null`/`undefined` here is a document that has never been saved,
  // which is an empty form rather than an error.
  return <>{children(query.data ?? null)}</>;
}

/**
 * Why the save did not land, and whether trying again is worth anything.
 *
 * Never the raw error. `getUserMessage` is the one place upstream text is turned
 * into something a founder can act on, and a Postgres or PostgREST string is not
 * that. The modal stays open behind this with everything typed still in it —
 * closing on a failed save would discard the work and claim success.
 */
function SaveFailure({ error }: { error: unknown }) {
  const colors = useThemeColors();
  if (!error) return null;

  const message = getUserMessage(error);
  // A cancelled operation is not a failure and has no message to show.
  if (!message) return null;

  return (
    <View
      className="rounded-2xl p-3 gap-1.5"
      style={{ backgroundColor: colors.traffic.redLight, overflow: 'hidden' }}
      accessibilityRole="alert"
      accessibilityLabel={`Save failed. ${message}`}
    >
      {/* Only the TITLE is a flex row. The message below it is deliberately not
          in one: an error capped to a line to protect a sibling's width is an
          error whose actionable half is off-screen, and there is no sibling here
          to protect. */}
      <View className="flex-row items-center gap-2">
        <AlertTriangle size={16} color={colors.error} />
        <Text
          numberOfLines={1}
          className="flex-1 text-[13px] font-semibold"
          style={{ color: colors.error }}
        >
          Nothing was saved
        </Text>
      </View>
      <Text className="text-[12px] leading-4" style={{ color: colors.text.primary }}>
        {message}
      </Text>
      <Text className="text-[11px] leading-4" style={{ color: colors.text.secondary }}>
        {isRetryable(error)
          ? 'Your answers are still here — press save again.'
          : 'Your answers are still here. Check the values above before saving again.'}
      </Text>
    </View>
  );
}
